import React from 'react';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import TileTags from './tile-tags';
import Translate from '@utils/translation-functions';
import { FlexibleLinkAtom } from '@atoms/link-atoms/link-atoms';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { getTileImageParameters } from '../../mixins';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { MissingLocaleAtom } from '@atoms/text-atoms/text-atoms';
import { TileProps } from './tile.types';
import { trimDescription } from '@utils/utils';
import {
  TileContainer,
  TileContentContainer,
  TileContentInfo,
  TileDescription,
  TileImageContainer,
  TileTitle,
} from './tile.styles';

const descriptionCharLimit = 100;

const TileImage: React.FC<
  Pick<TileProps, 'gatsbyImageData' | 'imageUrl' | 'imageAlt' | 'tileType'>
> = ({ gatsbyImageData, imageUrl, imageAlt, tileType }) => {
  const gatsbyImage = !!gatsbyImageData ? getImage(gatsbyImageData) : null;

  if (gatsbyImage) {
    return (
      <GatsbyImage
        image={gatsbyImage}
        alt={imageAlt || 'tile image'}
        className="gatsby-image-pos"
      />
    );
  }
  if (imageUrl) {
    return (
      <img
        src={`${imageUrl}?${getTileImageParameters(tileType)}`}
        alt={imageAlt}
      />
    );
  }
  return null;
};

const TileContent: React.FC<
  Pick<
    TileProps,
    | 'title'
    | 'description'
    | 'tileType'
    | 'resourceContentType'
    | 'isCompact'
    | 'tags'
    | 'tagLimit'
    | 'translate'
  >
> = ({
  title,
  description,
  resourceContentType,
  isCompact,
  tags,
  tagLimit,
  translate,
}) => {
  const typesTranslated = resourceContentType?.map(type => {
    return Translate(type, translate);
  });

  return (
    <TileContentContainer>
      {typesTranslated && (
        <TileContentInfo>{typesTranslated.join(' | ')}</TileContentInfo>
      )}
      {title && <TileTitle className="tile-title">{title}</TileTitle>}
      <TileDescription>
        {description && description.length > descriptionCharLimit
          ? trimDescription(description, descriptionCharLimit)
          : description}
      </TileDescription>
      {isCompact && !!tags && tags.length > 0 && (
        <TileTags tags={tags} tagLimit={tagLimit!} translate={translate!} />
      )}
    </TileContentContainer>
  );
};

const Tile: React.FC<TileProps> = ({
  isCompact = false,
  gqlContentType = '',
  tileType = 'standard',
  contentType = 'article',
  resourceContentType = [''],
  url = '',
  title = '',
  description = '',
  gatsbyImageData = {},
  imageUrl = '',
  imageAlt = '',
  imageFormat = '',
  tags = [],
  tagLimit = 3,
  eventDate = '',
  isLive = false,
  localeCode = 'en-GB',
  localeUnavailable = false,
  translate = true,
  loading = false,
  onClick,
  className = '',
  tabIndex = 0,
}) => {
  resourceContentType = resourceContentType?.filter(
    r => r !== 'Other/None applicable'
  );
  tags = tags?.filter(tag => tag.title !== 'None applicable');

  if (loading) {
    return (
      <TileContainer
        className={`${className} ${tileType}`}
        tileType={tileType}
        isCompact={isCompact}
      >
        <div className="loading">
          <LoadingSpinner />
        </div>
      </TileContainer>
    );
  }

  return (
    <>
      {url && (
        <TileContainer
          role="link"
          className={`${className} ${tileType}`}
          onClick={onClick}
          onKeyDown={onClick}
          tabIndex={tabIndex}
          tileType={tileType}
          isCompact={isCompact}
        >
          <FlexibleLinkAtom url={url} gtmId={GTM_ID.TILE_BLOCK}>
            <TileImageContainer
              tileType={tileType}
              localeUnavailable={localeUnavailable}
              isCompact={isCompact}
            >
              {/* <EventDetails eventDate={eventDate} isLive={isLive} /> */}
              <TileImage
                gatsbyImageData={gatsbyImageData}
                imageUrl={imageUrl}
                imageAlt={imageAlt}
                tileType={tileType}
              />
              {localeUnavailable && (
                <MissingLocaleAtom
                  localeCode={localeCode}
                  translate={translate}
                />
              )}
            </TileImageContainer>
            <TileContent
              title={title}
              description={description}
              tileType={tileType}
              resourceContentType={resourceContentType}
              isCompact={isCompact}
              tags={tags}
              tagLimit={tagLimit}
              translate={translate}
            />
          </FlexibleLinkAtom>
          {!isCompact && !!tags && tags.length > 0 && (
            <TileTags tags={tags} tagLimit={tagLimit!} translate={translate!} />
          )}
        </TileContainer>
      )}
    </>
  );
};

export default Tile;
